import { Box, Chip } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { chipSx, expandChipSx, getTagsContainerSx, tagsInnerContainerSx } from './styles';
import { OptionWithId, SELECT_ALL_OPTION, SelectAllOption } from './types';

type TagsRendererProps<OptionType extends OptionWithId> = {
  tagValue: (OptionType | SelectAllOption)[];
  isExpanded: boolean;
  onExpandToggle: () => void;
  getOptionLabel: (option: OptionType) => string;
  onChange: (newValue: OptionType[]) => void;
};

export const TagsRenderer = <OptionType extends OptionWithId>({
  tagValue,
  isExpanded,
  onExpandToggle,
  getOptionLabel,
  onChange,
}: TagsRendererProps<OptionType>) => {
  const filteredTags = useMemo(
    () => tagValue.filter((v): v is OptionType => v.id !== SELECT_ALL_OPTION.id),
    [tagValue],
  );

  const visibleTags = useMemo(
    () => (isExpanded ? filteredTags : filteredTags.slice(0, 2)),
    [isExpanded, filteredTags],
  );

  const remainingCount = filteredTags.length - 2;

  const handleChipDelete = useCallback(
    (option: OptionType) => {
      return (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onChange(filteredTags.filter((v) => v.id !== option.id));
      };
    },
    [filteredTags, onChange],
  );

  const handleExpandClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onExpandToggle();
    },
    [onExpandToggle],
  );

  return (
    <Box sx={getTagsContainerSx(isExpanded)}>
      <Box sx={tagsInnerContainerSx(isExpanded)}>
        {visibleTags.map((option) => (
          <Chip
            key={option.id}
            label={getOptionLabel(option)}
            size="small"
            onDelete={handleChipDelete(option)}
            sx={chipSx}
          />
        ))}
        {!isExpanded && remainingCount > 0 && (
          <Chip label={`+${remainingCount}`} size="small" onClick={handleExpandClick} sx={expandChipSx} />
        )}
      </Box>
    </Box>
  );
};
